import { Button, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { getAdminUserDisplayName } from "./adminRolesPermissionsMappers";
import type { AdminUser } from "./adminUsersAccessData";
import {
  DEACTIVATE_USER_DESCRIPTION,
  REVOKE_ALL_STUDY_ACCESS_DESCRIPTION,
} from "./adminUsersAccessMessaging";

const adminModalShellProps = {
  style: { top: 0, padding: 0, paddingBottom: 0 },
  wrapClassName: "no-padding-modal",
};

export default function DeactivateUserModal({
  user,
  variant = "direct",
  onClose,
  onConfirm,
  isSubmitting = false,
  submitError = null,
}: {
  user: AdminUser | null;
  variant?: "direct" | "revoke-all-trials";
  onClose: () => void;
  onConfirm?: () => Promise<void> | void;
  isSubmitting?: boolean;
  submitError?: string | null;
}) {
  const displayName = user ? getAdminUserDisplayName(user) : "";
  const description =
    variant === "revoke-all-trials"
      ? REVOKE_ALL_STUDY_ACCESS_DESCRIPTION
      : DEACTIVATE_USER_DESCRIPTION;

  return (
    <Modal
      open={user !== null}
      onCancel={onClose}
      width={740}
      centered
      {...adminModalShellProps}
      footer={null}
      closeIcon={null}
      destroyOnHidden
      styles={{
        container: {
          padding: 0,
          borderRadius: 8,
          overflow: "hidden",
          boxShadow: "0px 4px 20px 0px rgba(0,0,0,0.10)",
        },
        body: { padding: 0 },
      }}
    >
      {user ? (
        <div
          data-testid="deactivate-user-modal"
          className="flex h-[326px] flex-col justify-between bg-white"
        >
          <div>
            <div className="flex h-12 items-start border-b border-[#0000000f] pt-3">
              <div className="flex h-6 w-full items-center justify-between px-3">
                <h2 className="m-0 text-lg font-medium leading-none text-[#000000d9]">
                  Deactivate user
                </h2>
                <button
                  type="button"
                  aria-label="Close deactivate user"
                  className="flex h-5 w-5 items-center justify-center rounded text-[#394C54] hover:bg-[#EFF1F2]"
                  onClick={onClose}
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-6 bg-white p-4">
              <p className="m-0 w-full text-base font-normal leading-[19px] tracking-[-0.16px] text-[#2B4752]">
                {description}
              </p>

              <div className="grid h-24 w-full grid-cols-[1fr_1fr_150px_150px] overflow-hidden rounded border border-[#E7E6E4] bg-white shadow-[0px_2px_10px_2px_rgba(0,0,0,0.02)]">
                <div className="contents">
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    User
                  </div>
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    Email
                  </div>
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    Account type
                  </div>
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    Invited on
                  </div>
                </div>

                <div className="flex h-[60px] items-center gap-1.5 border-b border-[#0000000f] bg-white px-2.5 py-2">
                  <AvatarWithInitials
                    name={displayName}
                    avatarSize={20}
                    avatarFontSize={10}
                    showLabel={false}
                  />
                  <span className="text-sm font-normal leading-5 tracking-[-0.14px] text-[#000000d9]">
                    {displayName}
                  </span>
                </div>
                <div className="flex h-[60px] items-center border-b border-[#0000000f] bg-white px-2.5 py-2">
                  <span className="text-sm font-normal leading-5 tracking-[-0.14px] text-[#070B0D]">
                    {user.email}
                  </span>
                </div>
                <div className="flex h-[60px] items-center border-b border-[#0000000f] bg-white px-2.5 py-2">
                  <span className="text-sm font-normal leading-5 tracking-[-0.14px] text-[#070B0D]">
                    {user.accountType === "ADMIN" ? "Admin" : "User"}
                  </span>
                </div>
                <div className="flex h-[60px] items-center border-b border-[#0000000f] bg-white px-2.5 py-3">
                  <span className="text-sm font-normal leading-none tracking-[-0.14px] text-[#0F1923]">
                    {user.invitedOn}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex h-14 items-center justify-between border-t border-[#0000000f] p-3">
            <Button
              className="h-8 rounded border-[#00000073] px-3 font-medium text-[#2B4752]"
              disabled={isSubmitting}
              onClick={onClose}
            >
              Cancel
            </Button>
            {submitError ? (
              <span
                role="alert"
                className="mx-3 min-w-0 flex-1 truncate text-right text-sm text-[#980000]"
              >
                {submitError}
              </span>
            ) : null}
            <Button
              danger
              type="primary"
              className="h-8 rounded bg-[#980000]! px-2 font-medium text-white shadow-none hover:bg-[#7f0000]!"
              loading={isSubmitting}
              onClick={() => {
                if (onConfirm) {
                  void onConfirm();
                  return;
                }
                onClose();
              }}
            >
              Deactivate
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
